import pack from '../dist/content.json';

/** Типы контента. Форма гарантирована схемой в scripts/build.mjs. */
export interface Point {
  x: number;
  y: number;
}

export interface ZoneObject extends Point {
  kind: string;
  w: number;
  h: number;
  label?: string;
  tint?: string;
}

export interface ZoneExit extends Point {
  to: string;
  label: string;
  /** Размер полосы выхода в клетках. */
  w: number;
  h: number;
  /** Куда попадаешь в соседней зоне. */
  arrive: Point;
  /** Дорога сверх ходьбы, в игровых минутах. */
  minutes: number;
}

export interface Zone {
  id: string;
  title: string;
  size: { width: number; height: number };
  spawn: Point;
  terrain: string[];
  objects: ZoneObject[];
  /** Места, где по ночам появляются тачки. */
  carSpots: Point[];
  /** Маршруты мусорских патрулей. */
  patrols: { route: Point[] }[];
  exits: ZoneExit[];
}

export type StatKey = 'sila' | 'smekalochka' | 'obayanie' | 'chotkost' | 'avtoritet';

export interface DeedRequire extends Partial<Record<StatKey, number>> {
  cash?: number;
  /** Репутация с NPC: мамка ниже нуля котлет не даст. */
  npc?: { id: string; gte: number };
  level?: number;
  /** Закрытый квест: бригаду без разрешения смотрящего не собрать. */
  quest?: string;
}

interface DeedBase {
  id: string;
  title: string;
  hint?: string;
  /** kind объекта, к которому надо подойти. */
  object: string;
  /** Пусто — дело доступно везде, где стоит такой объект. */
  zones: string[];
  minutes: number;
  heat: number;
  stress: number;
  require?: DeedRequire;
  /** Одноразовое на объект: тачку второй раз не вскроешь. */
  once: boolean;
  /** Ветка дел: по ней прикладываются бонусы класса. */
  branch?: string;
}

export interface JobDeed extends DeedBase {
  type: 'job';
  difficulty: number;
  stats: Partial<Record<StatKey, number>>;
  /** Деньги сразу — или хабар, который ещё надо сбыть. */
  payout?: number;
  loot?: { def: string; title: string; value: number };
}

export interface SellDeed extends DeedBase {
  type: 'sell';
  commission: number;
}

export interface BuyDeed extends DeedBase {
  type: 'buy';
  price: number;
  business: { def: string; title: string; incomePerHour: number; heatPerNight: number };
}

export interface TrainDeed extends DeedBase {
  type: 'train';
  price: number;
  stat: StatKey;
}

export interface RestDeed extends DeedBase {
  type: 'rest';
  stressRelief: number;
  healthRelief: number;
  /** Отсидеться дома: палево спадает быстрее, но день уходит. */
  heatRelief: number;
  /** Дискач за деньги; котлеты и диван бесплатны. */
  price: number;
}

export interface CourtDeed extends DeedBase {
  type: 'court';
  price: number;
  difficulty: number;
}

export interface DateDeed extends DeedBase {
  type: 'date';
}

export interface ShopDeed extends DeedBase {
  type: 'shop';
}

export interface GarageDeed extends DeedBase {
  type: 'garage';
}

export interface TalkDeed extends DeedBase {
  type: 'talk';
  npc: string;
  reputation: number;
}

export interface GiftDeed extends DeedBase {
  type: 'gift';
  npc: string;
  price: number;
  reputation: number;
}

export interface BribeDeed extends DeedBase {
  type: 'bribe';
  price: number;
  heatRelief: number;
}

export interface TakeoverDeed extends DeedBase {
  type: 'takeover';
  territory: string;
}

export interface HireDeed extends DeedBase {
  type: 'hire';
  member: string;
}

export interface SettleDeed extends DeedBase {
  type: 'settle';
}

export interface Territory {
  id: string;
  title: string;
  holder: string;
  /** Доля с пассивного дохода, в процентах. */
  incomeBonus: number;
  retakeAfterDays: number;
  holdAvtoritet: number;
  enemy: { name: string; sila: number; chotkost: number; health: number; count: number; weapon?: number };
}

export interface BrigadeMember {
  member: string;
  title: string;
  price: number;
  sila: number;
  chotkost: number;
  health: number;
  /** Доля с пассивного дохода, в процентах. */
  share: number;
  hint?: string;
}

export interface FightDeed extends DeedBase {
  type: 'fight';
  enemy: { name: string; sila: number; chotkost: number; health: number; count: number; weapon?: number };
  payout: number;
  avtoritet: number;
}

export interface Npc {
  id: string;
  title: string;
  object: string;
  zone?: string;
  wants: string;
  gives: string;
}

/** Условие квеста или шага: проверяется по документу сейва. */
export interface Condition {
  stat?: StatKey;
  gte?: number;
  cash?: number;
  npc?: string;
  item?: string;
  vehicle?: string;
  business?: string;
  companion?: boolean;
  counter?: string;
  level?: number;
}

export interface QuestStep {
  id: string;
  text: string;
  done: Condition;
}

export interface Quest {
  id: string;
  title: string;
  hint: string;
  unlock: Condition[];
  steps: QuestStep[];
  reward: { cash?: number; xp?: number; avtoritet?: number; item?: string; text: string };
}

export interface Vehicle {
  def: string;
  title: string;
  price: number;
  /** Множитель времени дороги между зонами. */
  travelFactor: number;
  /** Сколько хабара влезает. */
  capacity: number;
  /** Приметность: патруль замечает такую тачку дальше. */
  flashiness: number;
  avtoritet: number;
  hint?: string;
}

export type Deed =
  | JobDeed
  | SellDeed
  | BuyDeed
  | TrainDeed
  | RestDeed
  | CourtDeed
  | DateDeed
  | ShopDeed
  | GarageDeed
  | TalkDeed
  | GiftDeed
  | BribeDeed
  | FightDeed
  | TakeoverDeed
  | HireDeed
  | SettleDeed;

export type GearSlot = 'kostyum' | 'obuv' | 'cep' | 'persten' | 'barsetka' | 'voda' | 'partak';

export interface Item {
  def: string;
  title: string;
  slot: GearSlot;
  price: number;
  stats?: Partial<Record<StatKey, number>>;
  quality?: number;
  chotkostPenalty?: number;
  shameChance?: number;
  set?: string;
  hint?: string;
}

export interface ItemSet {
  def: string;
  title: string;
  items: string[];
  effect: string;
  timeFactor?: number;
  walkFactor?: number;
  heatFactor?: number;
  sellFactor?: number;
  statPercent?: Partial<Record<StatKey, number>>;
  stressRelief?: number;
  commissionRelief?: number;
}

export interface ClassBonusData {
  branch?: string;
  payoutFactor: number;
  minutesFactor: number;
  heatFactor: number;
  statBonus?: Partial<Record<StatKey, number>>;
  healthBonus?: number;
  companionSlots?: number;
}

export interface Subclass extends ClassBonusData {
  id: string;
  title: string;
  description: string;
}

export interface PlayerClass extends ClassBonusData {
  id: string;
  title: string;
  stat: StatKey;
  description: string;
  subclasses: Subclass[];
}

export interface ContentPack {
  builtAt: string;
  zones: Zone[];
  deeds: Deed[];
  items: Item[];
  sets: ItemSet[];
  classes: PlayerClass[];
  vehicles: Vehicle[];
  npcs: Npc[];
  quests: Quest[];
  territories: Territory[];
  brigade: BrigadeMember[];
}

export const content = pack as ContentPack;

export function getZone(id: string): Zone {
  const zone = content.zones.find((z) => z.id === id);
  if (!zone) throw new Error(`Зона «${id}» не найдена в контент-паке`);
  return zone;
}

export function allZones(): Zone[] {
  return content.zones;
}

export function allDeeds(): Deed[] {
  return content.deeds;
}

/** Что можно сделать у этого объекта в этой зоне. */
export function deedsFor(objectKind: string, zoneId: string): Deed[] {
  return content.deeds.filter(
    (deed) => deed.object === objectKind && (deed.zones.length === 0 || deed.zones.includes(zoneId)),
  );
}

export function allItems(): Item[] {
  return content.items;
}

export function allSets(): ItemSet[] {
  return content.sets;
}

export function allClasses(): PlayerClass[] {
  return content.classes;
}

export function allVehicles(): Vehicle[] {
  return content.vehicles;
}

export function allNpcs(): Npc[] {
  return content.npcs;
}

export function allQuests(): Quest[] {
  return content.quests;
}

export function allTerritories(): Territory[] {
  return content.territories;
}

export function findTerritory(id: string): Territory | null {
  return content.territories.find((entry) => entry.id === id) ?? null;
}

export function allBrigade(): BrigadeMember[] {
  return content.brigade;
}

export function findBrigadeMember(member: string): BrigadeMember | null {
  return content.brigade.find((entry) => entry.member === member) ?? null;
}

export function findNpc(id: string): Npc | null {
  return content.npcs.find((npc) => npc.id === id) ?? null;
}

export function findVehicle(def: string | null): Vehicle | null {
  return def ? (content.vehicles.find((vehicle) => vehicle.def === def) ?? null) : null;
}

export function getItem(def: string): Item {
  const item = content.items.find((candidate) => candidate.def === def);
  if (!item) throw new Error(`Предмет «${def}» не найден в контент-паке`);
  return item;
}

export function findClass(id: string | null): PlayerClass | null {
  return id ? (content.classes.find((klass) => klass.id === id) ?? null) : null;
}

export function findSubclass(classId: string | null, subclassId: string | null): Subclass | null {
  const klass = findClass(classId);
  if (!klass || !subclassId) return null;
  return klass.subclasses.find((sub) => sub.id === subclassId) ?? null;
}

/** Есть ли у объекта хоть одно дело — по этому сцена решает, кликабелен ли он. */
export function hasDeeds(objectKind: string): boolean {
  return content.deeds.some((deed) => deed.object === objectKind);
}
